import React, { useState, useEffect } from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Paper,
  Typography,
  TextField,
} from "@mui/material";

const cellSx = {
  backgroundColor: "rgba(47, 53, 71, 0.9)",
  border: "1.5px solid #fff",
  color: "white",
  padding: "10px 8px",
};

const numberSx = {
  ...cellSx,
  textAlign: "left",
  fontWeight: 600,
};

const UnjustifiedDismissalIPSTable = ({
  declaredSalary = 0,
  nightBonus = 0,
  accruedVac = 0,
  proportionalVac = 0,
  notice = 0,
  advanceDeduction = 0,
  otherDeductions = 0,
  ipsSeverance = 0,
  total = 0,
  onValuesChange, // Optional callback to notify parent on edits
}) => {
  // Editable fields as strings for smooth user input
  const [editableDeclaredSalary, setEditableDeclaredSalary] = useState(
    String(declaredSalary)
  );
  const [editableAccruedVac, setEditableAccruedVac] = useState(
    String(accruedVac)
  );
  const [editableProportionalVac, setEditableProportionalVac] = useState(
    String(proportionalVac)
  );
  const [editableNotice, setEditableNotice] = useState(String(notice));
  const [editableIpsSeverance, setEditableIpsSeverance] = useState(
    String(ipsSeverance)
  );

  // Sync props changes to state
  useEffect(
    () => setEditableDeclaredSalary(String(declaredSalary)),
    [declaredSalary]
  );
  useEffect(() => setEditableAccruedVac(String(accruedVac)), [accruedVac]);
  useEffect(
    () => setEditableProportionalVac(String(proportionalVac)),
    [proportionalVac]
  );
  useEffect(() => setEditableNotice(String(notice)), [notice]);
  useEffect(
    () => setEditableIpsSeverance(String(ipsSeverance)),
    [ipsSeverance]
  );

  // Input change handler with validation for decimals and empty
  const handleInputChange = (setter, name) => (event) => {
    const val = event.target.value;
    if (val === "" || /^\d*\.?\d*$/.test(val)) {
      setter(val);
      if (typeof onValuesChange === "function") {
        onValuesChange({
          declaredSalary:
            name === "declaredSalary"
              ? Number(val) || 0
              : Number(editableDeclaredSalary) || 0,
          accruedVac:
            name === "accruedVac"
              ? Number(val) || 0
              : Number(editableAccruedVac) || 0,
          proportionalVac:
            name === "proportionalVac"
              ? Number(val) || 0
              : Number(editableProportionalVac) || 0,
          notice:
            name === "notice" ? Number(val) || 0 : Number(editableNotice) || 0,
          ipsSeverance:
            name === "ipsSeverance"
              ? Number(val) || 0
              : Number(editableIpsSeverance) || 0,
        });
      }
    }
  };

  // Calculate total dynamically with editable inputs and other props
  const correctedTotal =
    (Number(editableDeclaredSalary) || 0) +
    nightBonus +
    (Number(editableAccruedVac) || 0) +
    (Number(editableProportionalVac) || 0) +
    (Number(editableNotice) || 0) -
    advanceDeduction -
    otherDeductions +
    (Number(editableIpsSeverance) || 0);

  // Format numbers helper
  const formatNum = (num) =>
    num.toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

  const rows = [
    [
      "Salario Declarado(Mes)",
      <TextField
        value={editableDeclaredSalary}
        onChange={handleInputChange(
          setEditableDeclaredSalary,
          "declaredSalary"
        )}
        variant="standard"
        inputProps={{
          style: { color: "white", padding: "6px 8px", fontWeight: 600 },
          inputMode: "decimal",
          autoComplete: "off",
          spellCheck: false,
        }}
        sx={{
          "& .MuiInputBase-input": { textAlign: "left", color: "white" },
          width: "120px",
        }}
      />,
      "Declarado a IPS, durante el último mes",
    ],
    ["Adicional Nocturno", formatNum(nightBonus), "Del Mes"],
    [
      "Vacaciones Causadas",
      <TextField
        value={editableAccruedVac}
        onChange={handleInputChange(setEditableAccruedVac, "accruedVac")}
        variant="standard"
        inputProps={{
          style: { color: "white", padding: "6px 8px", fontWeight: 600 },
          inputMode: "decimal",
          autoComplete: "off",
          spellCheck: false,
        }}
        sx={{
          "& .MuiInputBase-input": { textAlign: "left", color: "white" },
          width: "100px",
        }}
      />,
      " >1 año de servicio",
    ],
    [
      "Vacaciones Proporcionales",
      <TextField
        value={editableProportionalVac}
        onChange={handleInputChange(
          setEditableProportionalVac,
          "proportionalVac"
        )}
        variant="standard"
        inputProps={{
          style: { color: "white", padding: "6px 8px", fontWeight: 600 },
          inputMode: "decimal",
          autoComplete: "off",
          spellCheck: false,
        }}
        sx={{
          "& .MuiInputBase-input": { textAlign: "left", color: "white" },
          width: "100px",
        }}
      />,
      "<1 año de servicio",
    ],
    [
      "Preaviso",
      <TextField
        value={editableNotice}
        onChange={handleInputChange(setEditableNotice, "notice")}
        variant="standard"
        inputProps={{
          style: { color: "white", padding: "6px 8px", fontWeight: 600 },
          inputMode: "decimal",
          autoComplete: "off",
          spellCheck: false,
        }}
        sx={{
          "& .MuiInputBase-input": { textAlign: "left", color: "white" },
          width: "100px",
        }}
      />,
      "Si no hay aviso, se paga aquí",
    ],
    [
      "Adelanto",
      advanceDeduction
        ? `-${advanceDeduction.toLocaleString(undefined, {
            minimumFractionDigits: 2,
          })}`
        : "-",
      "Si hubo adelanto",
    ],
    [
      "Otras Deducciones",
      otherDeductions
        ? `-${otherDeductions.toLocaleString(undefined, {
            minimumFractionDigits: 2,
          })}`
        : "-",
      "Otras deducciones autorizadas",
    ],
    [
      "Indemnizacion IPS",
      <TextField
        value={editableIpsSeverance}
        onChange={handleInputChange(setEditableIpsSeverance, "ipsSeverance")}
        variant="standard"
        inputProps={{
          style: { color: "white", padding: "6px 8px", fontWeight: 600 },
          inputMode: "decimal",
          autoComplete: "off",
          spellCheck: false,
        }}
        sx={{
          "& .MuiInputBase-input": { textAlign: "left", color: "white" },
          width: "120px",
        }}
      />,
      "Promedio últimos 6 meses [(salario + bono nocturno)/6]",
    ],
  ];

  return (
    <Box mt={4}>
      <Typography variant="h6" mb={2} fontWeight="bold" color="white">
        Despido Injustificado – IPS
      </Typography>
      <TableContainer
        component={Paper}
        sx={{ background: "rgba(47,53,71,0.9)" }}
      >
        <Table>
          <TableBody>
            {rows.map((row, idx) => (
              <TableRow key={idx}>
                <TableCell sx={cellSx}>{row[0]}</TableCell>
                {/* <TableCell
                  sx={{
                    ...numberSx,
                    color: row[0].toLowerCase().includes("deduction")
                      ? "red"
                      : row[0] === "IPS Severance"
                      ? "#3afa60"
                      : "white",
                    padding: ["Declared Salary (Month)", "Accrued Vacations", "Proportional Vacations", "Notice", "IPS Severance"].includes(row[0])
                      ? "6px 8px"
                      : "10px 8px",
                  }}
                >
                  {row[1]}
                  
                </TableCell> */}
                <TableCell
                  sx={{
                    ...numberSx,
                    color: /adelanto|deducciones/i.test(row[0])
                      ? "red"
                      : row[0] === "Indemnizacion IPS"
                      ? "#3afa60"
                      : "white",
                    padding: [
                      "Salario Declarado(Mes)",
                      "Vacaciones Causadas",
                      "Vacaciones Proporcionales",
                      "Preaviso",
                      "Indemnizacion IPS",
                    ].includes(row[0])
                      ? "6px 8px"
                      : "10px 8px",
                  }}
                >
                  {row[1]}
                </TableCell>

                <TableCell sx={cellSx}>{row[2]}</TableCell>
              </TableRow>
            ))}
            {/* Total row */}
            <TableRow>
              <TableCell sx={{ ...cellSx, fontWeight: 700 }}>
                Total a Pagar
              </TableCell>
              <TableCell
                colSpan={2}
                sx={{
                  ...numberSx,
                  background: "rgba(34,189,110,0.15)",
                  color: "#3afa60",
                  fontWeight: "bold",
                  textAlign: "center",
                  fontSize: 20,
                }}
              >
                {correctedTotal.toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default UnjustifiedDismissalIPSTable;
